import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Trash, MagnifyingGlass } from "@phosphor-icons/react";

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState("");

  const load = (query) => api.get("/admin/users", { params: { q: query || undefined } }).then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const submitSearch = (e) => { e.preventDefault(); load(q); };

  const setRole = async (u, role) => {
    setMsg("");
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      setMsg(`${u.email} is now ${role}.`);
      load(q);
    } catch (e) {
      setMsg("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    setMsg("");
    try {
      await api.delete(`/admin/users/${u.id}`);
      setMsg(`Deleted ${u.email}.`);
      load(q);
    } catch (e) {
      setMsg("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="px-6 md:px-12 py-8">
      <h2 className="text-2xl font-black tracking-tighter uppercase font-[Outfit] mb-4">Users <span className="text-zinc-400">({users.length})</span></h2>
      {msg && <div data-testid="users-msg" className="border border-zinc-900 bg-yellow-100 px-4 py-2 text-xs uppercase tracking-widest font-bold font-[Outfit] mb-4">{msg}</div>}

      <form onSubmit={submitSearch} className="flex gap-2 mb-4">
        <input data-testid="users-search-input" className="input-brutal flex-1" placeholder="Search by email or name…" value={q} onChange={e => setQ(e.target.value)} />
        <button data-testid="users-search-btn" className="btn-brutal btn-blue"><MagnifyingGlass size={14} weight="bold" className="mr-2" /> Search</button>
      </form>

      <div className="border border-zinc-900 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-white text-xs uppercase tracking-widest font-[Outfit]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Joined</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-6 text-center text-zinc-500">No users found</td></tr>
            ) : users.map(u => {
              const isSelf = me && u.id === me.id;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-zinc-200">
                  <td className="px-4 py-3 font-bold">{u.name}{isSelf && <span className="tag ml-2">You</span>}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`user-role-select-${u.id}`}
                      className="border border-zinc-900 px-2 py-1 text-xs uppercase font-bold font-[Outfit] bg-white disabled:opacity-40"
                      value={u.role || "user"}
                      disabled={isSelf}
                      onChange={e => setRole(u, e.target.value)}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`user-delete-${u.id}`}
                      onClick={() => remove(u)}
                      disabled={isSelf}
                      className="w-9 h-9 border border-zinc-900 flex items-center justify-center hover:bg-[#ff3333] hover:border-[#ff3333] hover:text-white disabled:opacity-30 disabled:pointer-events-none"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
